import ApiService from './api.service'
import type { Issue, IssueStatus, IssuePriority } from '../types/issue'

/**
 * Response type for a single issue
 */
export interface IssueResponse extends Issue {}

/**
 * Response type for a list of issues
 */
export interface IssueListResponse extends Array<Issue> {}

/**
 * Data type for creating/updating an issue
 */
export interface IssueData {
  title: string
  description?: string
  status: IssueStatus
  priority: IssuePriority
  createdAt: string
  resolvedDate?: string
  assignedTo?: number
  resolutionNotes?: string
}

/**
 * Service for managing project delivery issues (reserves)
 */
class DeliveryIssuesService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Delivery Issues Error'
      }
    })
  }

  /**
   * Fetch issues for a specific project
   */
  async getIssues(projectId: string): Promise<IssueListResponse | null> {
    return this.get(`/api/project/${projectId}/issues`)
  }

  /**
   * Get open issues for a project
   */
  async getOpenIssues(projectId: string): Promise<IssueListResponse | null> {
    return this.get(`/api/project/${projectId}/issues/open`)
  }

  /**
   * Get critical issues for a project
   */
  async getCriticalIssues(projectId: string): Promise<IssueListResponse | null> {
    return this.get(`/api/project/${projectId}/issues/critical`)
  }

  /**
   * Get issues by status
   */
  async getIssuesByStatus(projectId: string, status: IssueStatus): Promise<IssueListResponse | null> {
    return this.get(`/api/project/${projectId}/issues/status/${status}`)
  }

  /**
   * Get issues within a date range
   */
  async getIssuesInDateRange(
    projectId: string,
    startDate: string,
    endDate: string
  ): Promise<IssueListResponse | null> {
    return this.get(`/api/project/${projectId}/issues/date-range`, {
      startDate,
      endDate
    })
  }

  /**
   * Count open issues for a project
   */
  async countOpenIssues(projectId: string): Promise<number | null> {
    return this.get(`/api/project/${projectId}/issues/count/open`)
  }

  /**
   * Get a specific issue by ID
   */
  async getIssueById(projectId: string, issueId: string): Promise<IssueResponse | null> {
    return this.get(`/api/project/${projectId}/issues/${issueId}`)
  }

  /**
   * Create a new issue
   */
  async createIssue(projectId: string, issueData: IssueData): Promise<IssueResponse | null> {
    return this.post(`/api/project/${projectId}/issues`, issueData)
  }

  /**
   * Update an existing issue
   */
  async updateIssue(
    projectId: string,
    issueId: string,
    issueData: IssueData
  ): Promise<IssueResponse | null> {
    return this.put(`/api/project/${projectId}/issues/${issueId}`, issueData)
  }

  /**
   * Delete an issue
   */
  async deleteIssue(projectId: string, issueId: string): Promise<void> {
    await this.delete(`/api/project/${projectId}/issues/${issueId}`)
  }

  /**
   * Get issue status history timeline
   */
  async getIssueStatusTimeline(projectId: string, issueId: string): Promise<any[] | null> {
    return this.get(`/api/project/${projectId}/issues/${issueId}/status-timeline`)
  }

  /**
   * Change issue status through state machine
   */
  async changeIssueStatus(
    projectId: string,
    issueId: string,
    newStatus: IssueStatus,
    comment?: string
  ): Promise<IssueResponse | null> {
    return this.post(`/api/project/${projectId}/issues/${issueId}/change-status`, {
      newStatus,
      comment
    })
  }
}

export default new DeliveryIssuesService()
